import { Injectable, inject } from "@angular/core";
import { AuthService } from "../services/auth.service";

/**
 * Item de navegação do menu global do header.
 * - `route`: destino de rota (já com `:id` resolvido).
 * - `openAction`: ação-modal a reproduzir na página dona (via query param `open`).
 *   Quando presente, o header navega para `route` passando `{ open: openAction }`,
 *   e o dashboard dono lê o param e abre o modal correspondente.
 * - `disabled`: item "em breve".
 */
export interface NavItem {
  label: string;
  icon: string; // chave do switch-icons
  route: string;
  openAction?: string;
  disabled?: boolean;
}

/**
 * Fonte única, role-aware, dos destinos de navegação do produto — o que antes
 * vivia hardcoded dentro de cada dashboard (`actions[]` no teacher; markup no
 * student). O header consome isto; os dashboards podem migrar num follow-up.
 */
@Injectable({ providedIn: "root" })
export class HeaderNavService {
  private readonly auth = inject(AuthService);

  /** Itens do menu para o papel/estado de autenticação atual. */
  items(): NavItem[] {
    if (!this.auth.isLoggedIn()) return this.guestItems();
    if (this.auth.isCandidate()) return this.candidateItems();
    return this.auth.isTeacher() ? this.teacherItems() : this.studentItems(this.auth.getUserId());
  }

  private guestItems(): NavItem[] {
    return [
      { label: "Início", icon: "home", route: "/home" },
      { label: "BF Academy", icon: "sparkles", route: "/academy" },
      // Spec 028: vagas abertas ou banco de talentos.
      { label: "Trabalhe Conosco", icon: "search", route: "/trabalhe-conosco" },
      // Spec 029: documentos jurídicos públicos.
      { label: "Termos de Uso", icon: "info", route: "/termos-de-uso" },
      { label: "Privacidade", icon: "info", route: "/privacidade" },
    ];
  }

  /** Candidato do Trabalhe Conosco (spec 028): não é da escola. */
  private candidateItems(): NavItem[] {
    return [
      { label: "Meu painel", icon: "home", route: "/candidato" },
      { label: "Vagas", icon: "search", route: "/trabalhe-conosco" },
      {
        label: "Termo de privacidade",
        icon: "info",
        route: "/trabalhe-conosco/privacidade",
      },
    ];
  }

  private teacherItems(): NavItem[] {
    const isManager = this.auth.isManager();
    return [
      { label: "Painel", icon: "home", route: "/teacher-dashboard" },
      {
        // A gerente vê a base inteira; a professora, só os alunos dela.
        label: isManager ? "Painel de Alunos" : "Meus Alunos",
        icon: "book-open",
        route: "/students",
      },
      { label: "Materiais", icon: "sparkles", route: "/supplies" },
      { label: "Artigos", icon: "book-open", route: "/artigos" },
      // Spec 027: postar vídeo aulas por módulo/tópico.
      { label: "Vídeo Aulas", icon: "play", route: "/video-aulas/gerenciar" },
      { label: "Agenda", icon: "calendar-days", route: "/agenda" },
      // Matrícula é ato de gestão: some do menu da professora (spec 011 RF2.1).
      // Recrutamento também (spec 028): vagas e banco de talentos.
      ...(isManager
        ? [
            {
              label: "Nova Matrícula",
              icon: "edit",
              route: "/students",
              openAction: "matricula",
            },
            { label: "Recrutamento", icon: "search", route: "/recrutamento" },
            // Spec 029: termos e políticas versionados.
            { label: "Jurídico", icon: "info", route: "/juridico" },
          ]
        : []),
      { label: "Meu perfil", icon: "user", route: "/perfil" },
    ];
  }

  private studentItems(id: string | null): NavItem[] {
    const panel = id ? `/student/${id}` : "/home";
    return [
      { label: "Painel", icon: "home", route: panel },
      {
        label: "Conteúdo Personalizado",
        icon: "sparkles",
        route: panel,
        openAction: "nivel-material",
      },
      // Vídeo aulas do nível do aluno (spec 027).
      { label: "Vídeo Aulas", icon: "play", route: "/video-aulas" },
      { label: "Artigos", icon: "book-open", route: "/artigos" },
      // Painel financeiro do aluno (spec 012 RF1).
      { label: "Meu Plano", icon: "check", route: "/meu-plano" },
      { label: "Meu perfil", icon: "user", route: "/perfil" },
    ];
  }
}
